/**
 * Reports day detail (side panel). Express `GET /reports/day` sends each
 * section's cell, every check that applied that day with its outcome, what
 * was logged and the day's exceptions — this only words it.
 */

import moment from 'moment'
import {
  FOOD_SAFETY_CHECKLIST_STEP_STATUS,
  FOOD_SAFETY_REPORT_ITEM_STATUS,
  FOOD_SAFETY_SCHEDULE_MODE,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_TASK_KIND,
} from 'src/constants/foodSafety'
import { formatFoodSafetyTemp } from 'src/utils/foodSafetyCorrectiveAction'
import {
  formatAdHocSkipStatusLabel,
  isAdHocSkipLog,
} from 'src/utils/foodSafetyAdHoc'
import { FOOD_SAFETY_REPORT_ROW_ORDER } from 'src/utils/foodSafetyReports'
import { joinDistinctBits } from 'src/utils/foodSafetyReportExceptions'
import {
  formatDeliveryBands,
  formatDeliveryRejection,
} from 'src/utils/foodSafetyDeliveryDrop'

export const FOOD_SAFETY_REPORT_ITEM_STATUS_LABELS = Object.freeze({
  [FOOD_SAFETY_REPORT_ITEM_STATUS.COMPLETED]: 'Done',
  [FOOD_SAFETY_REPORT_ITEM_STATUS.MISSED]: 'Missed',
  [FOOD_SAFETY_REPORT_ITEM_STATUS.NOT_DUE]: 'Not due yet',
  [FOOD_SAFETY_REPORT_ITEM_STATUS.OUT_OF_SERVICE]: 'Out of service',
  [FOOD_SAFETY_REPORT_ITEM_STATUS.CLOSED]: 'Closed',
  [FOOD_SAFETY_REPORT_ITEM_STATUS.LOGGED]: 'Logged',
  [FOOD_SAFETY_REPORT_ITEM_STATUS.NOT_SCHEDULED]: 'Not scheduled',
})

const joinBits = bits => bits.filter(Boolean).join(' · ')

const time = at => {
  if (!at) return null
  const m = moment(at)
  return m.isValid() ? m.format('HH:mm') : null
}

const reportTimestamp = at =>
  at && moment(at).isValid() ? moment(at).format('YYYY-MM-DD HH:mm') : null

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

/** "Tuesday 22 September" */
export function formatReportDayTitle(localDate) {
  const m = moment(localDate, 'YYYY-MM-DD', true)
  return m.isValid() ? m.format('dddd D MMMM') : localDate || 'Day detail'
}

/** "5 of 6 checks done · 1 missed · 1 out of service · 4 logs · 1 unresolved" */
export function formatReportDaySummary(summary) {
  if (!summary) return ''
  const due = Number(summary.checksDue) || 0
  const done = Number(summary.checksCompleted) || 0
  const missed = Number(summary.checksMissed) || 0
  const paused = Number(summary.outOfService) || 0
  const closed = Number(summary.closed) || 0
  const logs = Number(summary.logsTotal) || 0
  const raised = Number(summary.exceptions?.raised) || 0
  const open = Number(summary.exceptions?.unresolved) || 0
  return joinBits([
    due > 0 ? `${done} of ${due} checks done` : null,
    missed ? `${missed} missed` : null,
    summary.notRequiredCount
      ? `${summary.notRequiredCount} not required`
      : null,
    paused ? `${paused} out of service` : null,
    closed ? `${closed} closed` : null,
    logs ? plural(logs, 'log', 'logs') : null,
    raised
      ? open
        ? `${plural(raised, 'exception', 'exceptions')} · ${open} unresolved`
        : `${plural(raised, 'exception', 'exceptions')} · actioned`
      : null,
  ])
}

/** Log-as-you-go items are never scheduled — say they weren't used. */
const notRequiredItem = logs =>
  Array.isArray(logs) &&
  logs.length > 0 &&
  logs.every(log => log?.payload?.skipped === true)

export function formatReportDayItemStatus(status, { scheduleMode, logs } = {}) {
  if (notRequiredItem(logs)) return 'Not Required Today'
  if (
    status === FOOD_SAFETY_REPORT_ITEM_STATUS.NOT_SCHEDULED &&
    scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.AD_HOC
  ) {
    return 'Not logged this day'
  }
  return FOOD_SAFETY_REPORT_ITEM_STATUS_LABELS[status] || ''
}

export function reportDayItemTone(status, { logs } = {}) {
  if (notRequiredItem(logs)) return 'muted'
  switch (status) {
    case FOOD_SAFETY_REPORT_ITEM_STATUS.COMPLETED:
    case FOOD_SAFETY_REPORT_ITEM_STATUS.LOGGED:
      return 'ok'
    case FOOD_SAFETY_REPORT_ITEM_STATUS.MISSED:
      return 'alert'
    case FOOD_SAFETY_REPORT_ITEM_STATUS.OUT_OF_SERVICE:
      return 'paused'
    default:
      return 'muted'
  }
}

function isReportCleaningJob(log) {
  return (
    log?.taskKind === FOOD_SAFETY_TASK_KIND.JOB ||
    log?.configSnapshot?.taskKind === FOOD_SAFETY_TASK_KIND.JOB
  )
}

/** Check title in the day panel. A job is named as a job. */
export function formatReportDayItemName(item) {
  const name = String(item?.name || '').trim()
  if (item?.taskKind !== FOOD_SAFETY_TASK_KIND.JOB) return name
  return name ? `${name} · Job` : 'Job'
}

function readingBits(section, payload = {}, log) {
  if (
    section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS &&
    isReportCleaningJob(log)
  ) {
    return ['Job']
  }
  if (section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS) {
    const steps = Array.isArray(payload.stepCompletions)
      ? payload.stepCompletions
      : []
    if (!steps.length) return []
    const done = steps.filter(
      s =>
        s?.status === FOOD_SAFETY_CHECKLIST_STEP_STATUS.DONE ||
        (!s?.status && s?.completed),
    ).length
    const issues = steps.filter(
      s => s?.status === FOOD_SAFETY_CHECKLIST_STEP_STATUS.ISSUE,
    ).length
    return [
      `${done} of ${steps.length} steps done`,
      issues ? plural(issues, 'issue', 'issues') : null,
    ]
  }
  if (section === FOOD_SAFETY_SECTION.COOLING) {
    const start = formatFoodSafetyTemp(payload.startTemp)
    const end = formatFoodSafetyTemp(payload.finalTemp)
    const mins = Number(payload.coolingMinutes)
    return [
      start && end ? `${start} → ${end}` : end || start || null,
      mins > 0 ? `${mins} min` : null,
    ]
  }
  if (
    [
      FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
      FOOD_SAFETY_SECTION.DISHWASHERS,
      FOOD_SAFETY_SECTION.LEGACY_EQUIPMENT,
    ].includes(section)
  ) {
    if (section === FOOD_SAFETY_SECTION.LEGACY_EQUIPMENT)
      return [
        [
          formatFoodSafetyTemp(payload.value),
          formatFoodSafetyTemp(payload.boilingValue),
        ]
          .filter(Boolean)
          .join(' / '),
      ]
    const dishwasher = log?.configSnapshot?.equipmentKind === 'dishwasher'
    const second = formatFoodSafetyTemp(payload.boilingValue)
    return [
      dishwasher
        ? `Wash ${formatFoodSafetyTemp(payload.value)} · Rinse ${second}`
        : second
        ? `Ice water ${formatFoodSafetyTemp(
            payload.value,
          )} · Boiling water ${second}`
        : formatFoodSafetyTemp(payload.value) || null,
      typeof payload.calibrationPassed === 'boolean'
        ? payload.calibrationPassed
          ? 'Pass'
          : 'Fail'
        : null,
    ]
  }
  const held = Number(payload.heldMinutes)
  const started =
    section === FOOD_SAFETY_SECTION.HOT_HOLDING && payload.heldSinceAt
      ? moment(payload.heldSinceAt)
      : null
  const bandText = formatDeliveryBands(payload)
  const rejection = formatDeliveryRejection(payload)
  return [
    bandText || formatFoodSafetyTemp(payload.value) || null,
    started?.isValid()
      ? `holding started ${started.format('YYYY-MM-DD HH:mm')}`
      : null,
    section === FOOD_SAFETY_SECTION.HOT_HOLDING &&
    payload.heldMinutes != null &&
    held >= 0
      ? `held ${held} min`
      : null,
    rejection
      ? `Rejected · ${rejection}`
      : payload.accepted === false
      ? 'Rejected'
      : payload.accepted === true
      ? 'Accepted'
      : null,
  ]
}

/**
 * One saved log as recorded, e.g.
 * `{ when: '09:12', who: 'Sam', reading: '9°C', flagged: true, notes }`.
 */
export function formatReportDayLog(
  section,
  log,
  { withName = false, itemName = null } = {},
) {
  const payload = log?.payload || {}
  const skipped = isAdHocSkipLog(log)
  const name = withName ? log?.definitionName || null : null
  const skip = itemName ? [itemName] : []
  return {
    id: String(log?._id || `${log?.definitionId}-${log?.completedAt}`),
    when: time(log?.completedAt),
    entryAudit:
      log?.loggedLate ||
      (log?.createdAt &&
        new Date(log.createdAt) - new Date(log.completedAt) > 60000)
        ? `Late entry · Entered ${
            reportTimestamp(log.createdAt) || 'at an unknown time'
          } by ${String(log.completedByName || '').trim() || 'Unknown staff'}`
        : null,
    cookingKind:
      log?.configSnapshot?.cookingKind === 'cooking'
        ? 'Cook'
        : log?.configSnapshot?.cookingKind === 'reheating'
        ? 'Reheat'
        : 'Unknown',
    who: String(log?.completedByName || '').trim() || null,
    supplier: String(payload.supplierName || '').trim() || 'Not recorded',
    product:
      String(payload.productName || '').trim() ||
      'Not specified (delivery-level record)',
    originalDeliveryName: !payload.productName ? log?.definitionName : null,
    deliveryReference: payload.purchaseOrderNumber
      ? /^po/i.test(payload.purchaseOrderNumber)
        ? payload.purchaseOrderNumber
        : `PO ${payload.purchaseOrderNumber}`
      : null,
    deliveryReading:
      readingBits(section, payload, log).filter(Boolean).join(' · ') ||
      'Not recorded',

    reading: skipped
      ? joinDistinctBits([name, formatAdHocSkipStatusLabel(section)], { skip })
      : joinDistinctBits(
          [
            name,
            payload.productName,
            payload.supplierName,
            ...readingBits(section, payload, log),
            ...(log?.correction?.corrected
              ? [
                  'Corrected',
                  log.correction.original?.finalTemp != null
                    ? `originally ${log.correction.original.finalTemp}°C`
                    : log.correction.original?.value != null
                    ? `originally ${log.correction.original.value}°C`
                    : null,
                ]
              : []),
          ],
          { skip },
        ) || 'Recorded',
    flagged: log?.outOfRange === true,
    notes: String(payload.notes || '').trim() || null,
  }
}

const hasContent = row =>
  (row?.items || []).length > 0 || (row?.otherLogs || []).length > 0

/**
 * Sections worth showing for the day (grid order); the section the user
 * opened is always kept so the panel never looks empty for it.
 */
export function listReportDaySections(day, { focusSection } = {}) {
  const rank = new Map(FOOD_SAFETY_REPORT_ROW_ORDER.map((key, i) => [key, i]))
  const exceptionSections = new Set(
    (day?.exceptions || []).map(e => e?.section).filter(Boolean),
  )
  return (day?.sections || [])
    .filter(row => rank.has(row?.section))
    .filter(
      row =>
        row.section === focusSection ||
        hasContent(row) ||
        exceptionSections.has(row.section),
    )
    .sort((a, b) => rank.get(a.section) - rank.get(b.section))
}

export function reportDaySectionExceptions(day, section) {
  return (day?.exceptions || []).filter(e => e?.section === section)
}

export function reportDayPanelDomId(section) {
  return `fs-day-section-${section}`
}
